import React from 'react';
import { useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormHelperText } from '@mui/material';
import { Switch, TextField } from 'app/components/FormikField/base';
import { FieldStyled, FormLabelStyled, GridItemStyled, GridRootStyled } from './EditEmployerForm.styles';
import type { OwnerDetail } from 'app/types';
import useEditEmployerForm from './useEditEmployerForm';
import { EmployerAddressFields } from './components/EmployerAddressFields';
import { EmployerContactFields } from './components/EmployerContactFields';
import { EmployerIdentifierFields } from './components/EmployerIdentifierFields';

type EditEmployerFormProps = {
  employerShowHolding: boolean;
  ownerData: OwnerDetail;
};

const EditEmployerForm = ({ employerShowHolding = true, ownerData }: EditEmployerFormProps) => {
  const intl = useIntl();
  const { errors, values, validateForm, setFieldValue, initialValues } = useFormikContext<any>();

  const {
    isPartOfHoldingChange,
    isPensioenbijIntegrationChange,
    employerStatusAutoComplete,
    parentIdAutoComplete,
    subOwnerIdAutoComplete,
    ownerAutoComplete,
  } = useEditEmployerForm({
    ownerData,
    validateForm,
    setFieldValue,
    initialValues,
  });

  return (
    <GridRootStyled container spacing={2}>
      {/* name */}
      <GridItemStyled item xs={4}>
        <FormLabelStyled>
          <FormattedMessage id="employers.name" />
          {' *'}
        </FormLabelStyled>
        <FieldStyled
          color="secondary"
          component={TextField}
          fullWidth
          name="employerName"
          placeholder={intl.formatMessage({ id: 'employers.name' }).toUpperCase()}
          required
          variant="outlined"
        />
      </GridItemStyled>
      <GridItemStyled item xs={4}>
        {employerShowHolding && (
          <div>
            <FormLabelStyled>
              <FormattedMessage id="employers.holdingPart.toggle" />
            </FormLabelStyled>
            <FieldStyled
              displayFlex
              color="primary"
              component={Switch}
              variant="outlined"
              onChange={isPartOfHoldingChange}
              name="isPartOfHolding"
              checked={values?.isPartOfHolding}
              type="checkbox"
            />
          </div>
        )}
      </GridItemStyled>
      <GridItemStyled item xs={4}>
        {employerShowHolding && (
          <div>
            <FormLabelStyled>
              <FormattedMessage id="employers.isPensioenbijIntegration.toggle" />
            </FormLabelStyled>
            <FieldStyled
              displayFlex
              color="primary"
              component={Switch}
              variant="outlined"
              onChange={isPensioenbijIntegrationChange}
              name="isPensioenbijIntegration"
              checked={values?.isPensioenbijIntegration}
              type="checkbox"
            />
          </div>
        )}
      </GridItemStyled>
      <GridItemStyled item xs={4}>
        <FormLabelStyled>
          <FormattedMessage id="employers.status" />
        </FormLabelStyled>
        {employerStatusAutoComplete}
      </GridItemStyled>
      <GridItemStyled item xs={4}>
        <FormLabelStyled>
          <FormattedMessage id="employers.owner" />
          {' *'}
        </FormLabelStyled>
        {ownerAutoComplete}
      </GridItemStyled>
      <GridItemStyled item xs={4}>
        <FormLabelStyled>
          <FormattedMessage id="employers.subOwner" />
        </FormLabelStyled>
        {subOwnerIdAutoComplete}
      </GridItemStyled>

      {values.isPartOfHolding && (
        <GridItemStyled item xs={4}>
          <FormLabelStyled>
            <FormattedMessage id="employers.holding.name" />
          </FormLabelStyled>
          {parentIdAutoComplete}
          {errors.parentId && (
            <FormHelperText className="Mui-error">
              <FormattedMessage id="validationMessage.mixed.required" />
            </FormHelperText>
          )}
        </GridItemStyled>
      )}

      <EmployerIdentifierFields />
      <EmployerAddressFields />
      <EmployerContactFields />
    </GridRootStyled>
  );
};

export default EditEmployerForm;
